<template>
  <nav class="horizontal-nav" ref="navRoot" role="navigation">
    <div id="horizontal-nav" :class="({collapsed})">
      <div v-for="group in menuItems" v-bind:class="'group group-' + group.title.toLowerCase().replace(' ', '-')">
        <NavList v-bind:group="group"></NavList>
      </div>
    </div>

    <button :class="({collapsed})" class="navbar-toggle" data-target="#horizontal-nav" type="button"
            @click.prevent="collapsed = !collapsed">
      <span class="sr-only">Toggle navigation</span>
      <span class="icon-bar"></span>
      <span class="icon-bar"></span>
      <span class="icon-bar"></span>
    </button>
  </nav>
</template>

<script setup>
// Hermes override of @apexl/icarus-core's HorizontalMenu.vue.
//
// ROOT CAUSE (Defect 2 — top-nav dropdowns never close):
// NavList.vue (core) wires each menu group up with Bootstrap 5's *Collapse*
// component (`data-bs-toggle="collapse"`), not the *Dropdown* component.
// Collapse only knows how to toggle its own target's `.show` class when its
// own trigger is clicked — by design (see Bootstrap docs) it never closes on
// an outside click, never closes on Escape, and never closes sibling menus
// unless they're wired into a shared `data-bs-parent` accordion, which
// NavList does not do. That's why Configuration / System / Users could all
// be open at once and nothing closed them. (NavList.vue even has a leftover
// commented-out jQuery snippet — `$('body').on('show.bs.collapse', ...)` —
// that hints this was once handled, but it's dead: it's commented out *and*
// this project never loads jQuery, only bootstrap's own JS bundle.)
//
// FIX: rather than restructure NavList's nested-collapse markup into
// Bootstrap's Dropdown component (a much larger, riskier change), we
// replicate the three missing close triggers at this wrapper level using
// Bootstrap's own Collapse JS API:
//   1. click outside the nav -> close every open menu
//   2. Escape key            -> close every open menu
//   3. opening one menu      -> close every other open menu that isn't an
//                                ancestor/descendant of the one opening
//                                (so nested submenus don't collapse their
//                                own already-open parent)
import { ref, onMounted, onBeforeUnmount } from "vue";
import NavList from "@apexl/icarus-core/src/components/nav/NavList.vue";
import bootstrap from "bootstrap/dist/js/bootstrap.bundle.js";

defineProps({
  menuItems: {
    type: Array,
    required: true,
  }

});

const collapsed = ref(true);
const navRoot = ref(null);

function closeOpenMenus(exceptRelatedTo = null) {
  if (!navRoot.value) return;
  navRoot.value.querySelectorAll('#horizontal-nav .collapse.show').forEach((el) => {
    if (
      exceptRelatedTo &&
      (el === exceptRelatedTo || el.contains(exceptRelatedTo) || exceptRelatedTo.contains(el))
    ) {
      return;
    }
    bootstrap.Collapse.getOrCreateInstance(el, { toggle: false }).hide();
  });
}

function handleDocumentClick(event) {
  if (navRoot.value && !navRoot.value.contains(event.target)) {
    closeOpenMenus();
  }
}

function handleKeydown(event) {
  if (event.key === 'Escape') {
    closeOpenMenus();
  }
}

function handleShow(event) {
  closeOpenMenus(event.target);
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleKeydown);
  navRoot.value?.addEventListener('show.bs.collapse', handleShow);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('keydown', handleKeydown);
  navRoot.value?.removeEventListener('show.bs.collapse', handleShow);
});

</script>

<style lang="scss">
@use '@apexl/icarus-core/src/scss/variables/brands.scss' as brands;


.horizontal-nav {
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  .group {
    padding-left: 0;
    padding-right: 0;
    font-size: 18px;
    font-weight: 300;

    &:hover {
      background: #eee;
    }

    a {
      padding: 15px 45px 15px 25px;
      display: block;
      text-decoration: none;
      position: relative;

      &.dropdown-toggle {
        &:after {
          display: block;
          top: 25px;
          right: 25px;
          position: absolute;
        }
      }
    }

    i {
      margin-right: 15px;
    }

    .menu-group {
      > ul {
        padding: 0;
        background: #ddd;
        z-index: 9;
        width: 200vw;
        position: absolute;
        margin-bottom: 0;
        margin-top: 9px;
        margin-left: -100vw;
        left: 100%;

        &.collapsed {
          display: none;
        }

        &.show {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
        }

        li {
          ul {
            background: #ddd;
            display: flex;
            flex-direction: row;
            padding-left: 40vw;

            li {
              list-style: none;
              padding: 10px 20px;

              &:hover {
                background: brands.$brand--c1;
                cursor: pointer;

                a {
                  color: brands.$brand--c1--hover-text;
                }
              }

              a {
                padding: 0;
              }
            }
          }
        }
      }
    }
  }

  #horizontal-nav {
    display: flex;
  }
}

@media(max-width: 1200px) {

  #horizontal-nav {
    flex-direction: column;

    &.collapsed {
      display: none;
    }
  }

  .horizontal-nav .group a {
    padding: 15px 30px 15px 15px;
  }

  .horizontal-nav .group a.dropdown-toggle:after {
    right: 10px;
  }
}

button.navbar-toggle {
  display: none;
}

@media(max-width: 1024px) {
  #horizontal-nav {
    display: none;

    &.show {
      display: flex !important;
      height: auto !important;
      flex-direction: column;
      justify-content: center;
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background: rgba(255, 255, 255, 0.95);
      z-index: 10;
    }
  }

  .horizontal-nav .group {
    width: 90%;
    max-width: 300px;
    margin: 0 auto;
  }

  button.navbar-toggle {
    display: block;
    width: 40px;
    height: 24px;
    position: absolute;
    top: 15px;
    right: 10px;
    border: 0;
    background: transparent;
    cursor: pointer;
    z-index: 20;

    .icon-bar {
      position: absolute;
      width: 100%;
      height: 4px;
      transition-timing-function: ease;
      transition-duration: .15s;
      transition-property: transform;
      border-radius: 4px;
      background-color: #8700cd;
      left: 0;
      top: 2px;

      &:nth-child(2) {
        top: 11px;
        transition-timing-function: ease;
        transition-duration: .15s;
        transition-property: transform, opacity;
      }

      &:nth-child(3) {
        top: 20px;
      }
    }

    &:not(.collapsed) {
      .icon-bar {
        transform: translate3d(0, 9px, 0) rotate(45deg);

        &:nth-child(2) {
          transform: rotate(-45deg) translate3d(-5.71429px, -6px, 0);
          opacity: 0;
        }

        &:nth-child(3) {
          transform: translate3d(0, -10px, 0) rotate(-45deg);
          opacity: 1;
        }
      }
    }
  }
}

</style>
